import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule, ErrorHandler } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule, HttpClientXsrfModule } from '@angular/common/http';
import { HttpModule } from '@angular/http';
import { FormlyModule } from '@ngx-formly/core';
import { FormlyBootstrapModule } from '@ngx-formly/bootstrap';
import { NgTempusdominusBootstrapModule } from 'ngx-tempusdominus-bootstrap';
import { DndModule } from 'ng2-dnd';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MatDialogModule } from '@angular/material/dialog';
import {
  AppComponent,
  ActionLinksPanelComponent,
  DataEntryExampleComponent,
  FooterComponent,
  HeaderComponent,
  LoginComponent,
  MainMenuComponent,
  ReallyClickModalComponent,
  SearchTestsComponent,
  ValidationResultPanelComponent,
  ExceptionExampleComponent,
  ErrorLogsComponent,
  ErrorLogComponent,
  ExamplesComponent,
  ExampleComponent,
  NotificationExampleComponent,
  SearchGridLinkDestinationExampleComponent,
  SearchGridFormatExampleComponent,
  UnsavedChangesDialogComponent,
  CreateAccountComponent,
  ExampleFormlyComponent,
  DevHelpComponent,
  ApplicationSettingsComponent,
  ApplicationSettingComponent,
  CheckpointsComponent,
  CheckpointComponent,
  RolesComponent,
  RoleComponent,
  UserComponent,
  SpkDateComponent,
  SpkCurrencyComponent,
  SpkTextComponent,
  SpkSelectComponent,
  SpkCheckboxComponent,
  SpkTextareaComponent,
  SpkPasswordComponent,
  SpkNumberComponent,
  SpkTabComponent,
  SpkTabsComponent,
  SpkTabPaneComponent,
  SpkTabPanesComponent,
  SpkCardComponent,
  UsersComponent,
  AddressComponent,
  SpkSearchGridComponent,
  SpkPagerComponent,
  SpkValueTextCheckboxListComponent,
  SpkQuantityComponent,
  DashboardComponent,
  UserWidgetSelectionComponent,
  WidgetComponent,
  CountWidgetComponent,
  AdminTypeLinksComponent,
  CountriesComponent,
  CountryComponent,
  SpkLookupComponent,
  SpkModalLookupComponent,
  SpkControlsExampleComponent,
  QueuesComponent,
  SpkPercentComponent,
  ProjectReadinessStageComponent,
  NewProjectComponent,
  ProjectSummaryComponent,
  ProjectSummaryReadinessComponent,
  ProjectThumbnailComponent,
  ProjectListComponent,
  LandingPageComponent,
  ProjectSummaryMaterialsComponent,
  ProjectMaterialsStageComponent,
  ProjectMaterialsSubstageComponent
} from './components';
import {
  ActionLinksPanelService,
  BusyService,
  CanDeactivateGuard,
  ClientSessionService,
  ExecuteCommandService,
  PageContainerService,
  ReallyClickService,
  ValidationResultsService,
  UnsavedChangesDialogService,
  NotificationService,
  SearchGridService,
  BrowserNavigateService,
  TabService,
  ClientAssetDataService
} from './services';
import { Notifier, ToastrNotifier, DateDtoPipe, StringSpacerPipe } from './utilities';
import { GlobalExceptionHandler } from './utilities/exceptions/GlobalExceptionHandler';
import { ReallyClickDirective } from './directives';
import { AppRoutingModule } from './app-routing.module';
import { ApiService } from './data/ApiService.generated';
import { getCurrentDebugContext } from '@angular/core/src/view/services';
import { PercentPipe } from '@angular/common';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { MatButtonModule, MatChipsModule, MatInputModule, MatIconModule, MatSelectModule, MatOptionModule } from '@angular/material';
import { ProjectSummaryExecutionComponent } from './components/project-summary-execution/project-summary-execution.component';
import { ProjectExecutionStageComponent } from './components/project-execution-stage/project-execution-stage.component';
import { ProjectCxplannerSummmaryComponent } from './components/project-cxplanner-summary/project-cxplanner-summary.component';
import { ProjectCxplannerStageComponent } from './components/project-cxplanner-stage/project-cxplanner-stage.component';
import { ProjectExecutionScurveComponent } from './components/project-execution-scurve/project-execution-scurve.component';
import { SCurveService } from './services/scurve.service';

export function minlengthValidationMessage(err, field) {
  return `Should have atleast ${field.templateOptions && field.templateOptions.minLength} characters`;
}

export function maxlengthValidationMessage(err, field) {
  return `This value should be less than ${field.templateOptions && field.templateOptions.maxLength} characters`;
}

export function minValidationMessage(err, field) {
  return `This value should be more than ${field.templateOptions && field.templateOptions.min}`;
}

export function maxValidationMessage(err, field) {
  return `This value should be less than ${field.templateOptions && field.templateOptions.max}`;
}

@NgModule({
  declarations: [
    AppComponent,
    ActionLinksPanelComponent,
    DataEntryExampleComponent,
    FooterComponent,
    HeaderComponent,
    LoginComponent,
    CreateAccountComponent,
    MainMenuComponent,
    ReallyClickModalComponent,
    SpkSearchGridComponent,
    SearchTestsComponent,
    UnsavedChangesDialogComponent,
    UsersComponent,
    ReallyClickDirective,
    SearchGridFormatExampleComponent,
    SearchGridLinkDestinationExampleComponent,
    SpkPagerComponent,
    ValidationResultPanelComponent,
    ExceptionExampleComponent,
    ErrorLogsComponent,
    ErrorLogComponent,
    ExamplesComponent,
    ExampleComponent,
    NotificationExampleComponent,
    ExampleFormlyComponent,
    DevHelpComponent,
    ApplicationSettingsComponent,
    ApplicationSettingComponent,
    AddressComponent,
    ExampleFormlyComponent,
    CheckpointsComponent,
    CheckpointComponent,
    SpkValueTextCheckboxListComponent,
    SpkModalLookupComponent,
    RolesComponent,
    RoleComponent,
    UserComponent,
    SpkQuantityComponent,
    DateDtoPipe,
    StringSpacerPipe,
    SpkLookupComponent,
    QueuesComponent,
    SpkDateComponent,
    SpkCurrencyComponent,
    SpkTextComponent,
    SpkSelectComponent,
    SpkCheckboxComponent,
    SpkTextareaComponent,
    SpkPasswordComponent,
    SpkNumberComponent,
    SpkTabComponent,
    SpkTabsComponent,
    SpkTabPaneComponent,
    SpkTabPanesComponent,
    SpkCardComponent,
    SpkPercentComponent,
    DashboardComponent,
    UserWidgetSelectionComponent,
    WidgetComponent,
    CountWidgetComponent,
    AdminTypeLinksComponent,
    CountriesComponent,
    CountryComponent,
    SpkControlsExampleComponent,
    LandingPageComponent,
    ProjectListComponent,
    ProjectThumbnailComponent,
    ProjectSummaryReadinessComponent,
    ProjectSummaryComponent,
    ProjectReadinessStageComponent,
    NewProjectComponent,
    ProjectSummaryMaterialsComponent,
    ProjectMaterialsStageComponent,
    ProjectMaterialsSubstageComponent,
    ProjectSummaryExecutionComponent,
    ProjectExecutionStageComponent,
    ProjectCxplannerSummmaryComponent,
    ProjectCxplannerStageComponent,
    ProjectExecutionScurveComponent
  ],
  imports: [
    FontAwesomeModule,
    BrowserModule.withServerTransition({ appId: 'ng-cli-universal' }),
    BrowserAnimationsModule,
    HttpClientModule,
    HttpClientXsrfModule,
    HttpModule,
    FormsModule,
    AppRoutingModule,
    ReactiveFormsModule,
    NgTempusdominusBootstrapModule,
    DndModule.forRoot(),
    MatDialogModule,
    FormlyModule.forRoot({
      validationMessages: [
        { name: 'required', message: 'This field is required' },
        { name: 'minlength', message: minlengthValidationMessage },
        { name: 'maxlength', message: maxlengthValidationMessage },
        { name: 'min', message: minValidationMessage },
        { name: 'max', message: maxValidationMessage },
      ],
    }),
    FormlyBootstrapModule,
    NgxChartsModule,
    MatChipsModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule
  ],
  entryComponents: [
    UnsavedChangesDialogComponent,
    ProjectExecutionScurveComponent
  ],
  providers: [
    ActionLinksPanelService,
    BrowserNavigateService,
    BusyService,
    CanDeactivateGuard,
    ClientSessionService,
    ExecuteCommandService,
    PageContainerService,
    ReallyClickService,
    ValidationResultsService,
    ApiService,
    UnsavedChangesDialogService,
    NotificationService,
    SearchGridService,
    TabService,
    DateDtoPipe,
    PercentPipe,
    { provide: ErrorHandler, useClass: GlobalExceptionHandler },
    { provide: Notifier, useClass: ToastrNotifier },
    ClientAssetDataService,
    SCurveService

  ],
  bootstrap: [AppComponent]
})
export class AppModule { }



// WEBPACK FOOTER //
// ./src/app/app.module.ts